import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import './Chart.css';

const CategoryChart = ({ data, loading, onCategoryClick, selectedCategory }) => {
  const [chartHeight, setChartHeight] = useState(400);
  const [outerRadius, setOuterRadius] = useState(120);
  
  useEffect(() => {
    const updateDimensions = () => {
      if (window.innerWidth <= 480) {
        setChartHeight(300);
        setOuterRadius(80);
      } else if (window.innerWidth <= 768) {
        setChartHeight(350);
        setOuterRadius(100);
      } else {
        setChartHeight(400);
        setOuterRadius(120);
      }
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);
  
  if (loading) {
    return <div className="chart-loading">Loading category data...</div>;
  }

  // Colors for pie chart segments
  const COLORS = [
    '#3b82f6', '#22c55e', '#ef4444', '#f59e0b', '#8b5cf6',
    '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
  ];

  // Transform data for Recharts
  // Note: Backend already includes "Non catégorisé" in categories array if there are uncategorized transactions
  const chartData = data && data.categories ? [...data.categories] : [];
  
  // Also add uncategorized if it exists separately (backward compatibility)
  // Only add if not already present in categories
  if (data && data.uncategorized > 0) {
    const hasUncategorized = chartData.some(c => 
      c.name === 'Non catégorisé' || c.name === 'Uncategorized' || c.name === 'Non categorisé'
    );
    if (!hasUncategorized) {
      chartData.push({
        name: 'Non catégorisé',
        amount: data.uncategorized,
        percentage: data.total > 0 ? (data.uncategorized / data.total) * 100 : 0,
      });
    }
  }

  // Check if we have any data to display
  if (!data || (chartData.length === 0 && (!data.total || data.total === 0))) {
    return <div className="chart-empty">No category data available</div>;
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  };

  const renderLabel = (entry) => {
    return `${entry.name} (${entry.percentage.toFixed(1)}%)`;
  };

  return (
    <div className="chart-container">
      <h3 className="chart-title">Spending by Category</h3>
      {onCategoryClick && (
        <p className="chart-hint" style={{ 
          fontSize: '0.875rem', 
          color: 'var(--text-secondary)', 
          marginTop: '0.5rem',
          fontStyle: 'italic'
        }}>
          💡 Click on a category segment or legend item to filter transactions
        </p>
      )}
      <div className="category-chart-wrapper">
        <ResponsiveContainer width="100%" height={chartHeight}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderLabel}
              outerRadius={outerRadius}
              fill="#8884d8"
              dataKey="amount"
              onClick={(data, index, e) => {
                if (onCategoryClick && chartData[index]) {
                  const category = chartData[index];
                  onCategoryClick(category.name || null);
                }
              }}
              style={{ cursor: onCategoryClick ? 'pointer' : 'default' }}
            >
              {chartData.map((entry, index) => {
                const isSelected = selectedCategory && (entry.name === selectedCategory);
                return (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={COLORS[index % COLORS.length]}
                    style={{ 
                      opacity: selectedCategory && !isSelected ? 0.3 : 1,
                      filter: isSelected ? 'brightness(1.2)' : 'none',
                      cursor: onCategoryClick ? 'pointer' : 'default',
                      transition: 'opacity 0.2s, filter 0.2s'
                    }}
                  />
                );
              })}
            </Pie>
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'var(--bg-primary)', 
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
              }}
              labelStyle={{ color: 'var(--text-primary)' }}
              formatter={(value) => formatCurrency(value)}
            />
            <Legend 
              wrapperStyle={{ paddingTop: '20px', cursor: onCategoryClick ? 'pointer' : 'default' }}
              formatter={(value, entry) => `${value}: ${formatCurrency(entry.payload.amount)}`}
              onClick={(data) => {
                if (onCategoryClick && data.value) {
                  onCategoryClick(data.value);
                }
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="category-summary">
          <div className="summary-item">
            <span className="summary-label">Total Spending:</span>
            <span className="summary-value">{formatCurrency(data.total)}</span>
          </div>
          {data.uncategorized > 0 && (
            <div className="summary-item uncategorized">
              <span className="summary-label">Uncategorized:</span>
              <span className="summary-value">{formatCurrency(data.uncategorized)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryChart;
